import { css } from "@emotion/css";
import { borderRadius, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import * as React from "react";
interface HelperLink {
    type: "link";
    text: string;
    url: string;
    newTab?: boolean;
}
interface HelperCode {
    type: "code";
    text: string;
}
export type DescriptionContent = Array<string | HelperLink | HelperCode>;
/**
 * Validates if a URL is safe to use in links
 * @param url - The URL to validate
 * @returns true if the URL uses a safe protocol or is a relative path, false otherwise
 */
const isValidUrl = (url: string): boolean => {
    // Block dangerous protocols immediately
    if (url.startsWith("javascript:") || url.startsWith("data:") || url.startsWith("vbscript:")) {
        return false;
    }
    // Allow relative URLs (paths that don't start with a protocol)
    if (!url.includes("://")) {
        return true;
    }
    // For absolute URLs, validate the protocol
    try {
        const parsed = new URL(url);
        return ["http:", "https:", "mailto:", "tel:"].includes(parsed.protocol);
    }
    catch {
        return false;
    }
};
/**
 * Helper function to create a link object for use in description templates
 * @param text - The link text to display
 * @param url - The URL to link to (supports both absolute and relative URLs)
 * @param newTab - Whether to open the link in a new tab (defaults to false)
 * @returns HelperLink object for valid URLs, empty string for invalid URLs
 * @example
 * // Relative link (opens in same tab)
 * descriptionLink("Visit our docs", "/docs/getting-started")
 *
 * // Absolute link (opens in same tab)
 * descriptionLink("Visit our docs", "https://docs.example.com")
 *
 * // External link that opens in new tab
 * descriptionLink("External resource", "https://external.com", true)
 *
 * // Invalid/dangerous URLs return empty string
 * descriptionLink("Bad link", "javascript:alert('xss')") // returns ""
 */
export const descriptionLink = (text: string, url: string, newTab: boolean = false): HelperLink | "" => {
    if (!isValidUrl(url)) {
        return "";
    }
    return {
        type: "link",
        text,
        url,
        newTab,
    };
};
/**
 * Helper function to create a code object for use in description templates
 * @param text - The code text to display
 * @returns HelperCode object
 * @example
 * // Inline code snippet
 * code("propName")
 *
 * // Code with special characters
 * code("useState()")
 */
export const descriptionCode = (text: string): HelperCode => ({
    type: "code",
    text,
});
/**
 * For backwards compatibility
 * @deprecated Use `link` instead
 */
export const descriptionWithLink = descriptionLink;
/**
 * Tagged template literal function for creating rich descriptions with embedded links and code.
 * Allows natural template literal syntax with embedded link() and code() calls.
 * @param strings - Template literal strings
 * @param values - Interpolated values (including link and code objects)
 * @returns DescriptionContent array
 * @example
 * // Using template literal syntax with links
 * descriptionText`Visit our ${descriptionLink("documentation", "/docs")} for more info.`
 *
 * // With code snippets
 * descriptionText`Set the ${code("isVisible")} prop to control visibility.`
 */
export const descriptionText = (strings: TemplateStringsArray, ...values: (string | HelperLink | HelperCode)[]): DescriptionContent => {
    const isValidValue = (value: string | HelperLink | HelperCode): boolean => typeof value === "string" || (value && typeof value === "object" && (value.type === "link" || value.type === "code"));
    const isNonEmptyString = (item: string | HelperLink | HelperCode): boolean => typeof item !== "string" || item !== "";
    return strings
        .flatMap((str, index) => [str, ...(values[index] ? [values[index]] : [])])
        .filter(isValidValue)
        .filter(isNonEmptyString);
};
/**
 * Renders a description array containing strings, links, and code snippets
 * @param content - Array of strings, HelperLink, and HelperCode objects
 * @returns JSX element containing the rendered content
 */
export const renderDescription = (content: DescriptionContent): React.ReactElement => {
    const renderItem = (item: string | HelperLink | HelperCode, index: number): React.ReactElement => {
        if (typeof item === "string") {
            return <React.Fragment key={index}>{item}</React.Fragment>;
        }
        if (item.type === "link") {
            // Double-check URL safety during rendering as an additional security layer
            if (!isValidUrl(item.url)) {
                // Unsafe URL detected - render as plain text instead of link for security
                return <span key={index}>{item.text}</span>;
            }
            return (<a key={index} href={item.url} rel="noopener noreferrer" className={linkStyles} target={item.newTab ? "_blank" : undefined}>
                    {item.text}
                </a>);
        }
        if (item.type === "code") {
            return (<code key={index} className={codeStyles}>
                    {item.text}
                </code>);
        }
        return <React.Fragment key={index}/>;
    };
    return <>{content.map(renderItem)}</>;
};
const linkStyles = css({
    color: themeTokens.color.text.link.default,
    textDecoration: "underline",
    "&:hover": {
        textDecoration: "none",
        color: themeTokens.color.text.link.hover,
    },
    "&:focus-visible": {
        boxShadow: themeTokens.shadow.focused,
    },
});
const codeStyles = css({
    font: text.code.regular.medium,
    backgroundColor: themeTokens.color.background.tertiary,
    border: `1px solid ${themeTokens.color.border.secondary}`,
    borderRadius: borderRadius.extraSmall,
    padding: `${space[2]} ${space[4]}`,
    color: themeTokens.color.text.primary,
});
